/**
 * utils.js — Core utility functions
 * All section rendering calls and shared helpers.
 */

/**
 * Fetch one or more section HTML strings from Shopify.
 * @param {string} url      - Page URL to render in context of
 * @param {string[]} sections - Array of section IDs
 * @returns {Promise<Object>} - Map of sectionId => HTML string
 */
async function fetchSection(url, sections) {
  const separator = url.includes('?') ? '&' : '?';
  const params = `sections=${sections.join(',')}`;

  const response = await fetch(`${url}${separator}${params}`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  });

  if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
  const json = await response.json();
  /* Shopify returns { sections: { id: html } }; older themes used a flat map */
  return json.sections != null ? json.sections : json;
}

/**
 * Replace innerHTML of a target element with fresh section HTML.
 * Extracts the inner content of the section wrapper.
 * @param {string} targetSelector - CSS selector for the target element
 * @param {string} html           - Raw HTML string from section rendering
 * @param {string} [innerSelector] - Optional selector to extract specific content
 */
function renderSection(targetSelector, html, innerSelector) {
  const target = document.querySelector(targetSelector);
  if (!target) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const source = innerSelector
    ? doc.querySelector(innerSelector)
    : doc.body.firstElementChild;

  if (source) target.innerHTML = source.innerHTML;
}

/**
 * Debounce helper — delays function execution until after a pause.
 * @param {Function} fn    - Function to debounce
 * @param {number} [delay] - Delay in milliseconds (default: 300)
 * @returns {Function}
 */
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Format money according to Shopify money format.
 * @param {number} cents - Price in cents
 * @returns {string}
 */
function formatMoney(cents) {
  const format = window.theme?.moneyFormat || '${{amount}}';
  const amount = (cents / 100).toFixed(2);
  const amountNoDecimals = Math.floor(cents / 100);
  const amountWithComma = amount.replace('.', ',');

  return format
    .replace('{{amount_with_comma_separator}}', amountWithComma)
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
    .replace('{{amount}}', amount);
}
